<script setup lang="ts">
import { computed } from 'vue'
import { useStore } from '~/store'
import { useI18n } from 'vue-i18n'
import { momentFormat } from '~/utils/filters'
import defaultCover from '~/assets/audio/default-cover.png'

import { to } from '~/routing'

import PlayButton from '~/components/audio/PlayButton.vue'
import Layout from '~/ui/Layout.vue'
import Card from '~/ui/Card.vue'
import Link from '~/ui/Link.vue'
import Spacer from '~/ui/Spacer.vue'

import { type Album } from '~/types'

interface Props {
  album: Album;
}

const { t } = useI18n()

const props = defineProps<Props>()

const { album } = props

const artistCredit = album.artist_credit || []

const store = useStore()
const imageUrl = computed(() => props.album.cover?.urls.original
  ? store.getters['instance/absoluteUrl'](props.album.cover?.urls.medium_square_crop)
  : defaultCover
)
</script>

<template>
  <Card
    :title="album.title"
    :image="imageUrl"
    :tags="album.tags"
    v-bind="to({ name: 'library.albums.detail', params: { id: album.id } })"
    ui-inline-size="208"
  >
    <template #topright>
      <PlayButton
        icon-only
        :is-playable="!!album.is_playable"
        :album
      />
    </template>
    <!-- Todo: use ui-indicate-overflow here -->
    <Spacer :size="8" />
    <Layout
      flex
      gap-4
    >
      <template
        v-for="ac in artistCredit"
        :key="ac.artist.id"
      >
        <Link
          class="align-start color-default"
          v-bind="to({ name: 'library.artists.detail', params: { id: ac.artist.id } })"
          ui-no-underline
        >
          {{ ac.credit ?? t('components.Queue.meta.unknownArtist') }}
        </Link>
        <!-- TODO: Use font-class here -->
        <span style="font-weight: 600;">{{ ac.joinphrase }}</span>
      </template>
    </Layout>

    <template #footer>
      <span class="font-small layout-row" ui-block-size="40" style="padding-inline-start: 16px;">
        <span v-if="album.release_date">
            {{ momentFormat(new Date(album.release_date), 'Y') }}
        </span>
        <Icon icon="bi:dot" />
        <span ui-nowrap>
            {{ t('components.audio.album.Card.meta.tracks', album.tracks_count) }}
        </span>
      </span>
      <Spacer grow />
      <PlayButton
        dropdown-only
        discrete
        :is-playable="!!album.is_playable"
        :album
      />
    </template>
  </Card>
</template>
